import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  InputAdornment,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewList as ListIcon,
  ViewKanban as KanbanIcon,
  MoreVert as MoreVertIcon,
  NotificationsActive as ReminderIcon,
  TaskAlt as TaskIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  Flag as FlagIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import LeadService from "../services/LeadService";
import ContactApiService from "../services/ContactApiService";
import ConversationService from "../services/ConversationService";
import MyAccountService from "../services/MyAccountService";
import LeadDetailPanel from "../components/LeadDetailPanel";
import LeadRemindersEditor from "../components/LeadRemindersEditor";
import {
  LEAD_PRIORITIES,
  PURPLE,
  PURPLE_DARK,
  PAGE_BG,
  priorityColor,
  getStageAccent,
  getPriorityAccent,
  getCategoryColor,
  getInitials,
  timeAgo,
  formatDateTimeWithAgo,
  formatTime,
} from "../components/leadConstants";

const staffDisplayName = (row) =>
  `${row?.first_name || ""} ${row?.last_name || ""}`.trim() || row?.email || "Team member";

const LeadsView = ({ defaultView = "kanban" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialView = location.pathname.includes("/list") ? "list" : defaultView;

  const [viewMode, setViewMode] = useState(initialView);
  const [leads, setLeads] = useState([]);
  const [boardColumns, setBoardColumns] = useState([]);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [staff, setStaff] = useState([]);
  const [currentAccountId, setCurrentAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLead, setNewLead] = useState({
    title: "",
    contact_id: "",
    stage_id: "",
    category_id: "",
    description: "",
    priority: "normal",
    assignee_account_id: "",
  });
  const [draftReminders, setDraftReminders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuLead, setMenuLead] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  const suppressCardClickRef = useRef(false);

  const loadCategories = useCallback(async () => {
    try {
      const data = await LeadService.getCategories();
      setCategories(data);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (q) setSearchQuery(q);
  }, [location.search]);
  
  const loadStages = useCallback(async () => {
    const data = await LeadService.getPipelineStages();
    setStages(data);
    if (data.length > 0 && !newLead.stage_id) {
      setNewLead((prev) => ({ ...prev, stage_id: data[0].id }));
    }
  }, [newLead.stage_id]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await LeadService.getLeads({
        search: searchQuery || undefined,
        stageId: stageFilter || undefined,
        categoryId: categoryFilter || undefined,
        assigneeAccountId: assigneeFilter || undefined,
        page: pagination.page,
        limit: 25,
      });
      setLeads(data.leads || []);
      setPagination({
        page: data.page,
        totalPages: data.totalPages,
        total: data.total,
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load leads", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, stageFilter, categoryFilter, assigneeFilter, pagination.page]);

  const loadBoard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await LeadService.getLeadsBoard({
        search: searchQuery || undefined,
        categoryId: categoryFilter || undefined,
        assigneeAccountId: assigneeFilter || undefined,
      });
      const columns = data.columns || [];
      setBoardColumns(columns);
      const boardStages = columns
        .map((col) => col.stage)
        .filter((stage) => stage?.id);
      if (boardStages.length > 0) {
        setStages((prev) => (prev.length > 0 ? prev : boardStages));
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load pipeline", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, assigneeFilter]);

  const refresh = useCallback(() => {
    if (viewMode === "list") loadList();
    else loadBoard();
  }, [viewMode, loadList, loadBoard]);

  useEffect(() => {
    loadStages();
    loadCategories();
    (async () => {
      try {
        const [rows, account] = await Promise.all([
          ConversationService.getRoutingStaff(),
          MyAccountService.fetch(),
        ]);
        setStaff(Array.isArray(rows) ? rows : []);
        const accountId = account?.getId?.() || null;
        setCurrentAccountId(accountId);
        setNewLead((prev) => (
          prev.assignee_account_id ? prev : { ...prev, assignee_account_id: accountId || "" }
        ));
      } catch {
        setStaff([]);
      }
    })();
  }, [loadStages, loadCategories]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const leadId = new URLSearchParams(location.search).get("id");
    if (!leadId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const full = await LeadService.getLeadById(leadId);
        if (!cancelled) setSelectedLead(full);
      } catch {
        if (!cancelled) {
          setSnackbar({ open: true, message: "Failed to open lead", severity: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    const path = viewMode === "list" ? "/leads/list" : "/leads";
    if (location.pathname !== path) {
      navigate({ pathname: path, search: location.search }, { replace: true });
    }
  }, [viewMode, navigate, location.pathname, location.search]);

  const handleViewChange = (_, newView) => {
    if (newView) setViewMode(newView);
  };

  const openCreateDialog = async () => {
    try {
      const result = await ContactApiService.getContactsDetails({ page: 1, limit: 10000 });
      const list = result.success ? result.data.contacts : [];
      setContacts(list.map((c) => ({
        id: c.getId(),
        name: c.getName(),
        phoneNumber: c.getPhoneNumber(),
      })));
    } catch {
      setContacts([]);
    }
    setCreateDialogOpen(true);
  };

  const kanbanColumns = boardColumns.length > 0
    ? boardColumns
    : stages.map((stage) => ({ stage, leads: [] }));

  const effectiveStages = useMemo(() => {
    if (stages.length > 0) return stages;
    const fromBoard = kanbanColumns
      .map((col) => col.stage)
      .filter((stage) => stage?.id);
    const seen = new Set();
    return fromBoard.filter((stage) => {
      if (seen.has(stage.id)) return false;
      seen.add(stage.id);
      return true;
    });
  }, [stages, kanbanColumns]);

  const handleCreateLead = async () => {
    if (!newLead.title.trim() || !newLead.contact_id) return;
    try {
      const created = await LeadService.createLead({
        title: newLead.title.trim(),
        contact_id: newLead.contact_id,
        stage_id: newLead.stage_id || undefined,
        category_id: newLead.category_id || undefined,
        description: newLead.description.trim() || undefined,
        priority: newLead.priority || "normal",
        assignee_account_id: newLead.assignee_account_id || undefined,
      });
      await Promise.all(
        draftReminders.map((reminder) => LeadService.createReminder(created.id, {
          reminder_type: reminder.reminder_type || "notification",
          due_option: reminder.due_option,
          frequency: reminder.frequency,
          time_of_day: reminder.time_of_day,
          day_of_week: reminder.day_of_week,
          day_of_month: reminder.day_of_month,
          month: reminder.month,
        })),
      );
      setCreateDialogOpen(false);
      setNewLead({
        title: "",
        contact_id: "",
        stage_id: effectiveStages[0]?.id || stages[0]?.id || "",
        category_id: "",
        description: "",
        priority: "normal",
        assignee_account_id: currentAccountId || "",
      });
      setDraftReminders([]);
      setSnackbar({ open: true, message: "Lead created", severity: "success" });
      refresh();
    } catch {
      setSnackbar({ open: true, message: "Failed to create lead", severity: "error" });
    }
  };

  const applyLeadStageMove = (columns, leadId, targetStageId) => {
    let movedLead = null;
    const withoutLead = columns.map((col) => {
      const remaining = (col.leads || []).filter((lead) => {
        if (lead.id === leadId) {
          movedLead = lead;
          return false;
        }
        return true;
      });
      return { ...col, leads: remaining };
    });
    if (!movedLead) return columns;

    const targetStage = effectiveStages.find((s) => s.id === targetStageId);
    const updatedLead = {
      ...movedLead,
      stage_id: targetStageId,
      stage_name: targetStage?.name || movedLead.stage_name,
    };

    return withoutLead.map((col) => {
      if (col.stage?.id !== targetStageId) return col;
      return { ...col, leads: [...(col.leads || []), updatedLead] };
    });
  };

  const handleMoveStage = async (leadId, stageId, { optimistic = false } = {}) => {
    if (optimistic) {
      setBoardColumns((prev) => {
        const base = prev.length > 0
          ? prev
          : effectiveStages.map((stage) => ({ stage, leads: [] }));
        return applyLeadStageMove(base, leadId, stageId);
      });
    }
    try {
      await LeadService.updateLeadStage(leadId, stageId);
      if (!optimistic) refresh();
      if (selectedLead?.id === leadId) {
        const updated = await LeadService.getLeadById(leadId);
        setSelectedLead(updated);
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to move lead", severity: "error" });
      loadBoard();
    }
  };

  const handleDragStart = (e, lead, sourceStageId) => {
    suppressCardClickRef.current = true;
    setDraggedLead({ id: lead.id, sourceStageId });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.id);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStageId(stageId);
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    setDragOverStageId(null);
    const leadId = draggedLead?.id || e.dataTransfer.getData("text/plain");
    const sourceStageId = draggedLead?.sourceStageId;
    setDraggedLead(null);
    if (!leadId || sourceStageId === targetStageId) return;
    await handleMoveStage(leadId, targetStageId, { optimistic: true });
  };

  const handleLeadClick = async (lead) => {
    try {
      const full = await LeadService.getLeadById(lead.id);
      setSelectedLead(full);
    } catch {
      setSelectedLead(lead);
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await LeadService.deleteLead(leadId);
      setSelectedLead(null);
      refresh();
      setSnackbar({ open: true, message: "Lead deleted", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to delete lead", severity: "error" });
    }
  };

  const totalBoardLeads = kanbanColumns.reduce(
    (sum, col) => sum + (col.leads || []).length,
    0,
  );

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") refresh();
  };

  const renderCategoryChip = (lead) => {
    if (!lead.category_name && !lead.category_id) return null;
    const color = getCategoryColor(lead);
    return (
      <Chip
        label={lead.category_name || "Category"}
        size="small"
        sx={{
          height: 22,
          fontSize: "0.68rem",
          fontWeight: 600,
          bgcolor: `${color}18`,
          color,
          border: `1px solid ${color}44`,
        }}
      />
    );
  };

  const renderLeadCard = (lead, stageId) => {
    const isSelected = selectedLead?.id === lead.id;
    const priorityAccent = getPriorityAccent(lead.priority);
    const showPriority = lead.priority && lead.priority !== "normal";

    return (
      <Paper
        key={lead.id}
        elevation={0}
        draggable
        onDragStart={(e) => handleDragStart(e, lead, stageId)}
        onDragEnd={() => {
          setDraggedLead(null);
          setDragOverStageId(null);
          setTimeout(() => {
            suppressCardClickRef.current = false;
          }, 0);
        }}
        onClick={() => {
          if (!suppressCardClickRef.current) handleLeadClick(lead);
        }}
        sx={{
          cursor: "grab",
          opacity: draggedLead?.id === lead.id ? 0.55 : 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: isSelected ? PURPLE : "#e8eaf2",
          borderLeftWidth: 4,
          borderLeftColor: priorityAccent,
          bgcolor: isSelected ? "#faf9ff" : "#fff",
          boxShadow: isSelected
            ? `0 0 0 1px ${PURPLE}, 0 4px 12px rgba(92, 79, 214, 0.12)`
            : "0 1px 3px rgba(15, 23, 42, 0.06)",
          transition: "box-shadow 0.15s, border-color 0.15s, transform 0.15s",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(15, 23, 42, 0.1)",
            transform: "translateY(-1px)",
          },
          "&:active": { cursor: "grabbing" },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: PURPLE,
                flexShrink: 0,
              }}
            >
              {getInitials(lead.contact_name || lead.title)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5 }}>
                <Typography variant="body2" fontWeight={700} color={PURPLE_DARK} noWrap sx={{ flex: 1 }}>
                  {lead.title}
                </Typography>
                <IconButton
                  size="small"
                  sx={{ mt: -0.5, mr: -0.5, color: "text.secondary" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuAnchor(e.currentTarget);
                    setMenuLead(lead);
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {lead.contact_name}
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" noWrap>
                {lead.contact_phone}
              </Typography>
            </Box>
          </Box>

          {lead.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                mt: 1,
                lineHeight: 1.45,
              }}
            >
              {lead.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.25, gap: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {renderCategoryChip(lead)}
              {showPriority && (
                <Chip
                  icon={<FlagIcon sx={{ fontSize: "14px !important" }} />}
                  label={lead.priority}
                  size="small"
                  sx={{
                    height: 22,
                    textTransform: "capitalize",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    bgcolor: `${priorityAccent}18`,
                    color: priorityAccent,
                    "& .MuiChip-icon": { color: priorityAccent },
                  }}
                />
              )}
              {lead.open_task_count > 0 && (
                <Chip
                  icon={<TaskIcon sx={{ fontSize: "14px !important" }} />}
                  label={lead.open_task_count}
                  size="small"
                  sx={{ height: 22, fontSize: "0.68rem", bgcolor: "#f0f2f8" }}
                />
              )}
              {(lead.assignee_account_name || lead.assignee_account_id) && (
                <Chip
                  label={lead.assignee_account_name || "Assigned"}
                  size="small"
                  sx={{ height: 22, fontSize: "0.68rem", bgcolor: "#eef2ff", color: PURPLE_DARK }}
                />
              )}
              {lead.reminder_count > 0 && (
                <Chip
                  icon={<ReminderIcon sx={{ fontSize: "14px !important" }} />}
                  label={lead.reminder_count}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.68rem",
                    bgcolor: `${PURPLE}14`,
                    color: PURPLE_DARK,
                    "& .MuiChip-icon": { color: PURPLE },
                  }}
                />
              )}
            </Box>
            {lead.updated_at && (
              <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                  {formatTime(lead.updated_at)}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" sx={{ fontSize: "0.6rem" }}>
                  {timeAgo(lead.updated_at)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderKanban = () => (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        pb: 2,
        minHeight: "calc(100vh - 280px)",
        alignItems: "flex-start",
      }}
    >
      {kanbanColumns.map((col) => {
        const accent = getStageAccent(col.stage);
        const leadCount = (col.leads || []).length;
        const isDropTarget = dragOverStageId === col.stage.id;

        return (
          <Box
            key={col.stage.id}
            onDragOver={(e) => handleDragOver(e, col.stage.id)}
            onDragLeave={() => setDragOverStageId(null)}
            onDrop={(e) => handleDrop(e, col.stage.id)}
            sx={{
              minWidth: 272,
              maxWidth: 272,
              flex: "0 0 auto",
              borderRadius: 2.5,
              bgcolor: isDropTarget ? `${accent.accent}12` : accent.bg,
              border: "1px solid",
              borderColor: isDropTarget ? accent.accent : "#e4e7ef",
              outline: isDropTarget ? `2px dashed ${accent.accent}` : "none",
              outlineOffset: 2,
              transition: "background-color 0.15s, border-color 0.15s",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                bgcolor: accent.header,
                borderBottom: "1px solid #e8eaf2",
                borderRadius: "10px 10px 0 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: accent.accent,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="subtitle2" fontWeight={700} color={PURPLE_DARK} noWrap>
                  {col.stage.name}
                </Typography>
              </Box>
              <Chip
                label={leadCount}
                size="small"
                sx={{
                  height: 22,
                  minWidth: 28,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  bgcolor: accent.chip,
                  color: PURPLE_DARK,
                }}
              />
            </Box>

            <Box sx={{ p: 1.25, display: "flex", flexDirection: "column", gap: 1, minHeight: 140 }}>
              {(col.leads || []).map((lead) => renderLeadCard(lead, col.stage.id))}
              {leadCount === 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    border: "1px dashed #d5d9e3",
                    bgcolor: "rgba(255,255,255,0.55)",
                  }}
                >
                  <Typography variant="caption" color="text.disabled" sx={{ px: 2, textAlign: "center" }}>
                    Drop leads here
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const renderList = () => (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ border: "1px solid #e4e7ef", borderRadius: 2, overflow: "hidden" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "#f6f4fd" }}>
            {["Lead", "Contact", "Phone", "Category", "Assignee", "Priority", "Stage", "Tasks", "Updated", ""].map((label) => (
              <TableCell
                key={label || "actions"}
                align={label ? "left" : "right"}
                sx={{ fontWeight: 700, color: PURPLE_DARK, fontSize: "0.8rem" }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {leads.map((lead) => {
            const stageAccent = getStageAccent(
              effectiveStages.find((s) => s.id === lead.stage_id) || { name: lead.stage_name },
            );
            return (
              <TableRow
                key={lead.id}
                hover
                selected={selectedLead?.id === lead.id}
                sx={{
                  cursor: "pointer",
                  "&.Mui-selected": { bgcolor: "#faf9ff !important" },
                }}
                onClick={() => handleLeadClick(lead)}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: "0.65rem", bgcolor: PURPLE }}>
                      {getInitials(lead.contact_name || lead.title)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{lead.title}</Typography>
                      {lead.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: "block" }}>
                          {lead.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{lead.contact_name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{lead.contact_phone}</TableCell>
                <TableCell>
                  {lead.category_name ? (
                    <Chip
                      label={lead.category_name}
                      size="small"
                      sx={{
                        height: 22,
                        bgcolor: `${getCategoryColor(lead)}18`,
                        color: getCategoryColor(lead),
                      }}
                    />
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {lead.assignee_account_name || "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    label={lead.priority || "normal"}
                    size="small"
                    color={priorityColor(lead.priority)}
                    sx={{ textTransform: "capitalize", height: 22 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={lead.stage_name}
                    size="small"
                    sx={{ height: 22, bgcolor: stageAccent.chip, color: PURPLE_DARK }}
                  />
                </TableCell>
                <TableCell>{lead.open_task_count || 0}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {lead.updated_at ? formatDateTimeWithAgo(lead.updated_at) : "—"}
                </TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setMenuAnchor(e.currentTarget);
                      setMenuLead(lead);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          {leads.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                <Typography color="text.secondary">No leads found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ minHeight: "100%", bgcolor: PAGE_BG }}>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderBottom: "1px solid #e4e7ef",
          borderRadius: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} color={PURPLE_DARK}>
              Leads
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {viewMode === "kanban"
                ? `${totalBoardLeads} active lead${totalBoardLeads === 1 ? "" : "s"} across ${kanbanColumns.length} stages`
                : `${pagination.total} lead${pagination.total === 1 ? "" : "s"} in pipeline`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              size="small"
              sx={{
                bgcolor: "#f0f2f8",
                "& .MuiToggleButton-root": {
                  border: 0,
                  px: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  "&.Mui-selected": { bgcolor: "#fff", color: PURPLE, boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
                },
              }}
            >
              <ToggleButton value="kanban">
                <KanbanIcon sx={{ mr: 0.5 }} fontSize="small" />
                Board
              </ToggleButton>
              <ToggleButton value="list">
                <ListIcon sx={{ mr: 0.5 }} fontSize="small" />
                List
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              component={RouterLink}
              to="/leads/categories"
              variant="outlined"
              startIcon={<TuneIcon />}
              sx={{
                borderColor: "#d5d9e3",
                color: PURPLE_DARK,
                fontWeight: 600,
              }}
            >
              Categories
            </Button>
            <Button
              component={RouterLink}
              to="/leads/pipeline-stages"
              variant="outlined"
              startIcon={<TuneIcon />}
              sx={{
                borderColor: "#d5d9e3",
                color: PURPLE_DARK,
                fontWeight: 600,
              }}
            >
              Stages
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{
                bgcolor: PURPLE,
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(92, 79, 214, 0.25)",
                "&:hover": { bgcolor: PURPLE_DARK },
              }}
            >
              New Lead
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search by title, contact, or phone…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 220,
              maxWidth: 420,
              "& .MuiOutlinedInput-root": { bgcolor: "#f8f9fc", borderRadius: 2 },
            }}
          />
          {viewMode === "list" && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Stage</InputLabel>
              <Select
                value={stageFilter}
                label="Stage"
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <MenuItem value="">All stages</MenuItem>
                {effectiveStages.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Assigned to</InputLabel>
            <Select
              value={assigneeFilter}
              label="Assigned to"
              onChange={(e) => setAssigneeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="me">Assigned to me</MenuItem>
              {staff.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {staffDisplayName(row)}
                  {row.role === "owner" ? " (owner)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={refresh}
            sx={{ borderColor: "#d5d9e3", color: PURPLE_DARK, fontWeight: 600 }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: PURPLE }} />
            </Box>
          ) : viewMode === "kanban" ? renderKanban() : renderList()}

          {viewMode === "list" && pagination.totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
              <Button
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
              >
                Previous
              </Button>
              <Typography sx={{ alignSelf: "center" }}>
                Page {pagination.page} of {pagination.totalPages}
              </Typography>
              <Button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>

        {selectedLead && (
          <LeadDetailPanel
            lead={selectedLead}
            stages={effectiveStages}
            categories={categories}
            onClose={() => setSelectedLead(null)}
            onRefresh={async (leadId) => {
              const id = leadId || selectedLead.id;
              const updated = await LeadService.getLeadById(id);
              setSelectedLead(updated);
              refresh();
              return updated;
            }}
            onMoveStage={handleMoveStage}
            onDelete={handleDeleteLead}
            onMessage={(message, severity) => setSnackbar({ open: true, message, severity })}
          />
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuLead(null); }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2 } }}
      >
        <MenuItem
          onClick={() => {
            if (menuLead) handleLeadClick(menuLead);
            setMenuAnchor(null);
            setMenuLead(null);
          }}
        >
          <ListItemIcon><OpenIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open details</ListItemText>
        </MenuItem>
        {effectiveStages.length > 0 && (
          <>
            <Divider />
            <ListSubheader sx={{ fontWeight: 600, lineHeight: "32px" }}>
              Move to stage
            </ListSubheader>
            {effectiveStages.map((stage) => (
              <MenuItem
                key={stage.id}
                disabled={menuLead?.stage_id === stage.id}
                onClick={() => {
                  if (!menuLead) return;
                  handleMoveStage(menuLead.id, stage.id);
                  setMenuAnchor(null);
                  setMenuLead(null);
                }}
              >
                <ListItemText>{stage.name}</ListItemText>
              </MenuItem>
            ))}
          </>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuLead) handleDeleteLead(menuLead.id);
            setMenuAnchor(null);
            setMenuLead(null);
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete lead</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setDraftReminders([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: PURPLE_DARK }}>Create Lead</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Title"
            value={newLead.title}
            onChange={(e) => setNewLead({ ...newLead, title: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Contact</InputLabel>
            <Select
              value={newLead.contact_id}
              label="Contact"
              onChange={(e) => setNewLead({ ...newLead, contact_id: e.target.value })}
            >
              {contacts.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name} — {c.phoneNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Stage</InputLabel>
            <Select
              value={newLead.stage_id}
              label="Stage"
              onChange={(e) => setNewLead({ ...newLead, stage_id: e.target.value })}
            >
              {effectiveStages.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={newLead.category_id}
              label="Category"
              onChange={(e) => setNewLead({ ...newLead, category_id: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Assign to</InputLabel>
            <Select
              value={newLead.assignee_account_id}
              label="Assign to"
              onChange={(e) => setNewLead({ ...newLead, assignee_account_id: e.target.value })}
            >
              {staff.map((row) => (
                <MenuItem key={row.id} value={row.id}>
                  {staffDisplayName(row)}
                  {row.role === "owner" ? " (owner)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={newLead.description}
            onChange={(e) => setNewLead({ ...newLead, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={newLead.priority}
              label="Priority"
              onChange={(e) => setNewLead({ ...newLead, priority: e.target.value })}
            >
              {LEAD_PRIORITIES.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <LeadRemindersEditor
            reminders={draftReminders}
            draftMode
            onDraftChange={setDraftReminders}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateLead}
            sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: PURPLE_DARK } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      </Box>
    </Box>
  );
};

export default LeadsView;
